/**
 * /downhole/assemblies — the BHA register (fieldiq-api#882 `GET /assemblies`).
 *
 * THE VOCABULARY IS THE API'S. building → ready → in_hole → laid_down →
 * disassembled. The unrouted screen used dispatched / in_field / returned and
 * fell back to "Building" for anything else; an unknown status here is a slate
 * dash with no rail.
 *
 * NO DISPATCH DATE COLUMN. Dispatching stamps nothing (`ready → in_hole`
 * changes the status only), and the nearest key, `first_run_in_at`, is when
 * the first RUN went in — a different fact. The register shows what exists:
 * built (the ready stamp), first run in, laid down.
 *
 * Chip counts are /dashboard's `assemblies_by_status`, the API's figure for the
 * whole register. Every row opens the builder, which is read only for any
 * assembly that is not `building` — the API refuses string edits after that.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, Hammer, Layers, Loader2, Plus, Rocket, Search, Undo2 } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDownholeAssemblies, useDownholeDashboard, useDownholeRuns } from '../../hooks/useDownhole';
import { formatInstantDay } from '../../lib/calendar-date';
import { DH_ASSEMBLY_STATUS_SKIN, dhSkin } from '../../lib/downhole-labels';
import { formatDh } from '../../lib/downhole-units';
import { fmtQty } from '../../lib/display-labels';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { DH_ASSEMBLY_STATUSES } from '../../types/downhole';
import { DhBadge, DhEmpty, DhPanel } from './DownholeBits';
import { BillingPills, indicatorsFromRuns } from './billingIndicators';

const isStatus = (v: string | null): v is (typeof DH_ASSEMBLY_STATUSES)[number] =>
  !!v && (DH_ASSEMBLY_STATUSES as readonly string[]).includes(v);

function wellRig(well: string | null, rig: string | null): string {
  if (!well && !rig) return '—';
  return `${well || '—'} / ${rig || '—'}`;
}

export default function AssembliesScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const status = isStatus(statusParam) ? statusParam : '';
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');

  // The API searches name, assembly number, well and customer.
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const params = useMemo(() => ({ ...(status && { status }), ...(q && { q }) }), [status, q]);
  const listQuery = useDownholeAssemblies(params);
  const dashboardQuery = useDownholeDashboard();

  const assemblies = listQuery.data?.data ?? [];
  const meta = listQuery.data?.meta;
  const tz = meta?.time_zone ?? null;
  const units = meta?.unit_system ?? null;
  const listFailure = queryLoadFailure(listQuery);
  const byStatus = dashboardQuery.data?.data?.assemblies_by_status;
  const countsLoading = dashboardQuery.isLoading;
  const filtered = !!(status || q);
  const day = (v: string | null) => formatInstantDay(v, tz) ?? '—';
  // Billing indicators from one runs read, grouped here — not from API-A's
  // ticket_summary, which counts a different thing (billingIndicators.tsx).
  const runsQuery = useDownholeRuns(undefined, assemblies.length > 0);
  const fromRuns = useMemo(() => (runsQuery.data ? indicatorsFromRuns(runsQuery.data.data) : null), [runsQuery.data]);
  const billing = (a: (typeof assemblies)[number]) =>
    fromRuns ? fromRuns.get(a.id) ?? { needsTicket: 0, readyToSend: null, awaitingCustomer: 0, readyToInvoice: 0 } : null;

  const setStatus = (next: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next) nextParams.set('status', next);
    else nextParams.delete('status');
    setSearchParams(nextParams, { replace: true });
  };

  const openAssembly = (id: string) => navigate(`/downhole/assemblies/${id}/build`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openAssembly(id);
    }
  };

  const total = byStatus ? DH_ASSEMBLY_STATUSES.reduce((n, s) => n + (byStatus[s] ?? 0), 0) : undefined;
  const chips = [
    // "All" is the sum of the API's own per-status counts over an exhaustive
    // enum — the same rows, not a tally of the loaded (and filtered) list.
    { key: '', label: 'All', count: total },
    ...DH_ASSEMBLY_STATUSES.map((s) => ({ key: s, label: dhSkin(DH_ASSEMBLY_STATUS_SKIN, s).label, count: byStatus?.[s] })),
  ];

  return (
    <div data-testid="downhole-assemblies-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Downhole tools"
        title="Assemblies"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <button
            type="button"
            onClick={() => navigate('/downhole/assemblies/new/build')}
            className="fi-action fi-action--standard fi-action--primary"
          >
            <Plus className="size-4" aria-hidden="true" />
            New assembly
          </button>
        }
        stats={[
          {
            icon: <Hammer className="size-4" aria-hidden="true" />,
            label: 'Building',
            sub: 'In the shop',
            tone: 'accent',
            loading: countsLoading,
            value: displayValue(byStatus?.building),
          },
          {
            icon: <Layers className="size-4" aria-hidden="true" />,
            label: 'Ready',
            sub: 'Built, not sent',
            tone: 'success',
            loading: countsLoading,
            value: displayValue(byStatus?.ready),
          },
          {
            icon: <Rocket className="size-4" aria-hidden="true" />,
            label: 'In hole',
            sub: 'Running',
            tone: 'info',
            loading: countsLoading,
            value: displayValue(byStatus?.in_hole),
          },
          {
            icon: <Undo2 className="size-4" aria-hidden="true" />,
            label: 'Laid down',
            sub: 'Back in the shop',
            tone: 'neutral',
            loading: countsLoading,
            value: displayValue(byStatus?.laid_down),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="scrollbar-hide flex gap-2 overflow-x-auto" role="group" aria-label="Filter by status">
            {chips.map((chip) => {
              const active = status === chip.key;
              return (
                <button
                  key={chip.key || 'all'}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setStatus(chip.key)}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                    active
                      ? 'bg-fi-amber text-fi-amber-ink'
                      : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {chip.label}
                  {!countsLoading && (
                    <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>
                      {displayValue(chip.count)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <label className="relative block">
            <span className="sr-only">Search name, number, well or customer</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, number, well or customer"
              className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
            />
          </label>

          <DhPanel
            eyebrow="BHA register"
            title="Assemblies"
            testId="downhole-assembly-register"
            footer={
              !listQuery.isLoading && !listFailure && assemblies.length > 0 && filtered && total !== undefined
                ? `Showing ${assemblies.length} of ${total} assemblies`
                : undefined
            }
          >
            {listQuery.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading assemblies" />
              </div>
            ) : listFailure ? (
              <LoadFailureNotice error={queryFailureError(listQuery)} kind={listFailure} subject="assemblies" />
            ) : assemblies.length === 0 ? (
              <DhEmpty>{filtered ? 'No assembly matches these filters.' : 'There are no assemblies yet.'}</DhEmpty>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[1520px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr className="border-l-4 border-l-transparent">
                        <th scope="col" className="px-4 py-3">Assembly</th>
                        <th scope="col" className="px-4 py-3">Status</th>
                        <th scope="col" className="px-4 py-3">Customer</th>
                        <th scope="col" className="px-4 py-3">Well / Rig</th>
                        <th scope="col" className="px-4 py-3">Template</th>
                        <th scope="col" className="px-4 py-3 text-right">Tools</th>
                        <th scope="col" className="px-4 py-3 text-right">Length</th>
                        <th scope="col" className="px-4 py-3 text-right">Runs</th>
                        <th scope="col" className="px-4 py-3">Billing</th>
                        <th scope="col" className="px-4 py-3">Built</th>
                        <th scope="col" className="px-4 py-3">Laid down</th>
                        <th scope="col" className="w-[44px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {assemblies.map((a) => (
                        <tr
                          key={a.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => openAssembly(a.id)}
                          onKeyDown={rowKeys(a.id)}
                          className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${
                            dhSkin(DH_ASSEMBLY_STATUS_SKIN, a.status).rail
                          }`}
                        >
                          <td className="px-4 py-3">
                            <p className="whitespace-nowrap font-mono text-sm font-extrabold text-fieldiq-accent-signal">{a.assembly_number}</p>
                            <p className="mt-0.5 max-w-[18rem] truncate text-xs text-fieldiq-text">{a.name}</p>
                          </td>
                          <td className="px-4 py-3"><DhBadge map={DH_ASSEMBLY_STATUS_SKIN} value={a.status} /></td>
                          <td className="min-w-[12rem] px-4 py-3 text-sm text-fieldiq-text">{displayValue(a.customer_name)}</td>
                          <td className="min-w-[14rem] px-4 py-3 text-sm text-fieldiq-text">{wellRig(a.well_name, a.rig_name)}</td>
                          <td className="min-w-[12rem] px-4 py-3 text-sm text-fieldiq-text">{displayValue(a.template_name)}</td>
                          <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{fmtQty(a.component_count)}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm text-fieldiq-text">{formatDh(a.total_length_ft, 'length', units)}</td>
                          <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{fmtQty(a.run_count)}</td>
                          <td className="min-w-[11rem] px-4 py-3" data-testid={`billing-${a.id}`}><BillingPills ind={billing(a)} /></td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{day(a.built_at)}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{day(a.laid_down_at)}</td>
                          <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="space-y-3 p-4 md:hidden">
                  {assemblies.map((a) => (
                    <div
                      key={a.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => openAssembly(a.id)}
                      onKeyDown={rowKeys(a.id)}
                      className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-mono text-sm font-extrabold text-fieldiq-accent-signal">{a.assembly_number}</p>
                          <p className="mt-0.5 truncate text-sm font-bold text-white">{a.name}</p>
                        </div>
                        <DhBadge map={DH_ASSEMBLY_STATUS_SKIN} value={a.status} />
                      </div>
                      <p className="mt-2 text-xs text-fieldiq-text">{wellRig(a.well_name, a.rig_name)}</p>
                      <p className="mt-1 text-xs text-fieldiq-muted">
                        {fmtQty(a.component_count)} tools · {displayValue(a.customer_name)}
                      </p>
                      <div className="mt-2"><BillingPills ind={billing(a)} /></div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DhPanel>
        </div>
      </section>
    </div>
  );
}
